import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import InfoDisplay from "@/components/InfoDisplay";
import type { FacilityAgreement, Applicant, Consumer } from "../types";
import Layout from "../../shared/components/Layout";
import { formatDate, formatMonth } from '@/utils/format';
import { CustomerParams } from "../types";
import updateCustomoer from "../api/updateCustomer";
import { ChangePlanRequest } from "../types";
import clearLocalStorage from "@/utils/clearLocalStorage";
import SidebarNavigation from "@/components/SidebarNavigation";

const CustomerConfirm: React.FC = () => {
  const displayPlan = (plan: string): string => {
    if(plan === 'both') return `両眼プラン`
    if(plan === 'right') return `片眼プラン(右眼)`
    if(plan === 'left') return `片眼プラン(左眼)`
    return '';
  }

  const handleButtonClick = (orderId: string | undefined, event: React.MouseEvent<HTMLButtonElement>): void => {
    if(!orderId) {
      const confirmRegistration = window.confirm('新規契約対象のレンズ注文が存在しませんが、登録しますか？');
      if (!confirmRegistration) {
        event.preventDefault();
      }
    }
  };

  const navigate = useNavigate();

  const [customer, setCustomer] = useState({
    custCode: "",
  });
  const [orderId, setOrderId] = useState<string>();

  const [facilityAgreement, setFacilityAgreement] = useState<FacilityAgreement>();
  const [applicant, setApplicant] = useState<Applicant>({
    birthday: "",
    nameKana: "",
  });
  const [consumer, setConsumer] = useState<Consumer>({
    nameKana: "",
    relationship: "",
    isSameApplicant: true,
  });

  const[changePlanRequest, setChangePlanRequest] = useState<ChangePlanRequest>({
    afterPlan: "",
    changeMonth: "",
    isChangePlan: false,
  })
  
  const [usageFeeOccurMonth, setUsageFeeOccurMonth] = useState<string>("");
  const [usageFeeFirstPaymentMonth, setUsageFeeFirstPaymentMonth] = useState<string>("");

  useEffect(() => {
    const storedCustomer = localStorage.getItem("customer");
    if(storedCustomer) {
      setCustomer(JSON.parse(storedCustomer));
    }

    const storedApplicant = localStorage.getItem("applicant");
    if(storedApplicant) {
      setApplicant(JSON.parse(storedApplicant));
    }

    const storedConsumer = localStorage.getItem("consumer");
    if(storedConsumer) {
      setConsumer(JSON.parse(storedConsumer));
    }

    const storedFacilityAgreement = localStorage.getItem("facilityAgreement");
    if(storedFacilityAgreement) {
      setFacilityAgreement(JSON.parse(storedFacilityAgreement));
    }

    const storedChangePlanRequest = localStorage.getItem("changePlanRequest");
    if(storedChangePlanRequest) {
      setChangePlanRequest(JSON.parse(storedChangePlanRequest));
    }

    const storedUsageFeeOccurMonth = localStorage.getItem("usageFeeOccurMonth");
    if(storedUsageFeeOccurMonth) {
      setUsageFeeOccurMonth(JSON.parse(storedUsageFeeOccurMonth));
    }

    const storedUsageFeeFirstPaymentMonth = localStorage.getItem("usageFeeFirstPaymentMonth");
    if(storedUsageFeeFirstPaymentMonth) {
      setUsageFeeFirstPaymentMonth(JSON.parse(storedUsageFeeFirstPaymentMonth));
    }

    const storedOrderId = localStorage.getItem("orderId");
    if(storedOrderId) {
      setOrderId(storedOrderId);
    }
  }, []);

  const handleSubmit = async(e: React.MouseEvent<HTMLFormElement>) => {
    e.preventDefault()

    const params: CustomerParams = {
      changePlanRequest: {...changePlanRequest, orderId: orderId},
      applicant: applicant,
      consumer: consumer,
    }

    try {
      const res = await updateCustomoer(params, customer.custCode);
      if(res.status === 201) {
        clearLocalStorage();
        navigate(`/facility/facility_agreements/customers/${customer.custCode}/complete`)
      } else if(res.status !== 201) {
        navigate(`/facility/facility_agreements/customers/${customer.custCode}`);
        window.scrollTo(0, 0);
        console.error(`status: ${res.status}, message: ${res.statusText}`);
      }
    } catch (err: any) {
      console.log('An unexpected error occurred:', err.message);
    }
  }

  const renderPreviousPage = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    navigate(`/facility/facility_agreements/customers/${customer.custCode}`, { state: { fromConfirmPage: true } });
    window.scrollTo(0, 0);
  }

  return(
    <Layout>
      <main id="page-user_index" className="wrapper">
        <section className="sec-contact">
          <div className="inner">
            <h1 className="c-ttl__01">お客様登録情報変更入力内容確認</h1>
            <div className="contact-box">
              <div className="contact-box__left">
                <form onSubmit={handleSubmit}>
                  <div className="contact-box__form js-item" id="id-01">
                    <h2 className="c-ttl__02">お客様情報</h2>
                    <InfoDisplay label={"契約種別"} value={"施設型定額制契約"} />
                    <InfoDisplay label={"お客様ID"} value={customer.custCode} />
                  </div>
                  <div className="contact-box__form mbsp-29 js-item h-adr" id="id-02">
                    <h2 className="c-ttl__02">申込者情報</h2>
                    <InfoDisplay label="申込者名(カナ)" value={applicant.nameKana} />
                    <InfoDisplay label="申込者生年月日" value={formatDate(applicant.birthday)} />
                  </div>
                  <div className="contact-box__form mbsp-29 js-item h-adr" id="id-03">
                    <h2 className="c-ttl__02">レンズ使用者情報</h2>
                    <InfoDisplay label="使用者について" value={consumer.isSameApplicant ? "申込者と同一" : "申込者と異なる"} />
                    { !consumer.isSameApplicant &&
                      <>
                        <InfoDisplay label="使用者名(カナ)" value={consumer.nameKana} />
                        <InfoDisplay label="申込者との関係" value={consumer.relationship} />
                      </>
                    }
                  </div>
                  <div className="contact-box__form mbsp-29 js-item h-adr" id="id-04">
                    <h2 className="c-ttl__02">契約詳細</h2>
                    <div className="contact-box__item">
                      <label className="contact-box__ttl">
                        <span>ご契約プラン</span>
                      </label>
                      <div className="contact-box__ct">
                        {changePlanRequest.isChangePlan ?
                          <>
                            <p className="contact-box__desc mbpc-5 mbsp-5">{displayPlan(facilityAgreement?.plan ?? '')}</p> 
                            <p className="contact-box__desc">
                              {`→ ${displayPlan(changePlanRequest?.afterPlan ?? '')}`}
                              <br />
                              {`プラン変更月: ${formatMonth(changePlanRequest?.changeMonth ?? '')}`}
                            </p>
                          </>
                        :
                          <p className="contact-box__desc">{displayPlan(facilityAgreement?.plan ?? '')}</p>
                        }
                      </div>
                    </div>
                    <InfoDisplay label={"サービス利用開始日"} value={formatDate(facilityAgreement?.startDate)} />
                    <InfoDisplay label="サービス利用開始月" value={formatMonth(facilityAgreement?.startMonth ?? '')} />
                    <InfoDisplay label="利用料請求発生月" value={usageFeeOccurMonth} />
                    <InfoDisplay label="利用料初回決済月（月末）" value={usageFeeFirstPaymentMonth} />
                    <InfoDisplay label="レンズ注文" value={orderId ? "あり" : "なし"} />
                  </div>
                  <button type="submit" className="c-btn__01" onClick={(event) => handleButtonClick(orderId, event)}>この内容で登録する</button>
                </form>
                <div className="c-al--c">
                  <button className="c-btn__02" onClick={renderPreviousPage}>前のページに戻る</button>
                </div>
              </div>
              <SidebarNavigation items={[
                { id: "id-01", label: "お客様情報" },
                { id: "id-02", label: "申込者情報" },
                { id: "id-03", label: "使用者情報" },
                { id: "id-04", label: "契約詳細" },
                ]} />
            </div>
          </div>
        </section>
      </main>
    </Layout>
  )
};

export default CustomerConfirm;